import { renderAsync as renderDocxAsync } from 'docx-preview'
import { getDocument, GlobalWorkerOptions, TextLayer, type PDFDocumentProxy, type PDFPageProxy } from 'pdfjs-dist'
import * as React from 'react'
import {
	clearMarkedElements,
	getTargetPage,
	markBestBlockMatch,
	markRunsByText,
	markTextMatchInRoot,
	type DocumentHighlightTarget,
	type RegisteredTextRun,
} from '#app/components/document-highlight.ts'
import { Icon } from '#app/components/ui/icon.tsx'
import { cn } from '#app/utils/misc.tsx'

GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString()

export type { DocumentHighlightTarget } from '#app/components/document-highlight.ts'

export type DocumentViewerHandle = {
	highlightCitation: (target: DocumentHighlightTarget) => boolean
	clearHighlight: () => void
	scrollToPage: (pageNumber: number) => void
}

type DocumentViewerProps = {
	url: string
	title?: string
	initialHighlight?: DocumentHighlightTarget
	initialPageNumber?: number
	className?: string
}

type PdfPageRegistration = {
	pageNumber: number
	root: HTMLElement
	runs: RegisteredTextRun[]
}

function scrollElementIntoViewer(element: HTMLElement) {
	element.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' })
}

function useInitialHighlight(
	initialHighlight: DocumentHighlightTarget | undefined,
	highlightCitation: (target: DocumentHighlightTarget) => boolean,
	ready: boolean,
) {
	React.useEffect(() => {
		if (!ready || !initialHighlight) return
		const frame = window.requestAnimationFrame(() => highlightCitation(initialHighlight))
		return () => window.cancelAnimationFrame(frame)
	}, [highlightCitation, initialHighlight, ready])
}

function useInitialPageScroll(
	initialPageNumber: number | undefined,
	scrollToPage: (pageNumber: number) => void,
	ready: boolean,
) {
	React.useEffect(() => {
		if (!ready || !initialPageNumber) return
		const frame = window.requestAnimationFrame(() => scrollToPage(initialPageNumber))
		return () => window.cancelAnimationFrame(frame)
	}, [initialPageNumber, ready, scrollToPage])
}

function DocumentViewerShell({
	children,
	className,
	status,
}: {
	children: React.ReactNode
	className?: string
	status?: React.ReactNode
}) {
	return (
		<section
			className={cn(
				'relative isolate h-[75vh] min-h-[520px] overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 shadow-sm',
				className,
			)}
		>
			<style>{documentViewerCss}</style>
			<div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-16 bg-gradient-to-b from-slate-50 to-transparent" />
			<div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-12 bg-gradient-to-t from-slate-50 to-transparent" />
			{status ? <div className="absolute right-4 top-4 z-20">{status}</div> : null}
			{children}
		</section>
	)
}

function ViewerLoading({ label = 'Preparando vista previa' }: { label?: string }) {
	return (
		<div className="absolute inset-0 z-30 flex items-center justify-center bg-slate-50/85 backdrop-blur-sm">
			<div className="flex items-center gap-3 rounded-full border border-slate-200 bg-white px-4 py-2 shadow-sm">
				<Icon name="spinner" className="h-4 w-4 animate-spin text-slate-400" />
				<span className="text-xs font-medium text-slate-500">{label}</span>
			</div>
		</div>
	)
}

function ViewerError({ message }: { message: string }) {
	return (
		<div className="flex h-full items-center justify-center p-8">
			<div className="max-w-sm rounded-2xl border border-red-100 bg-white px-6 py-5 text-center shadow-sm">
				<div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-red-50">
					<Icon name="triangle-error" className="h-5 w-5 text-red-500" />
				</div>
				<p className="text-sm font-medium text-stone-800">No se pudo cargar el documento</p>
				<p className="mt-1 text-xs leading-5 text-stone-500">{message}</p>
			</div>
		</div>
	)
}

export const PdfFileViewer = React.forwardRef<DocumentViewerHandle, DocumentViewerProps>(function PdfFileViewer(
	{ url, initialHighlight, initialPageNumber, className },
	ref,
) {
	const containerRef = React.useRef<HTMLDivElement>(null)
	const pageRefs = React.useRef(new Map<number, PdfPageRegistration>())
	const [documentProxy, setDocumentProxy] = React.useState<PDFDocumentProxy | null>(null)
	const [error, setError] = React.useState<string | null>(null)
	const [loadedPages, setLoadedPages] = React.useState(0)

	const clearHighlight = React.useCallback(() => {
		if (containerRef.current) clearMarkedElements(containerRef.current)
	}, [])

	const highlightCitation = React.useCallback(
		(target: DocumentHighlightTarget) => {
			clearHighlight()
			const targetPage = getTargetPage(target)
			const pages = [...pageRefs.current.values()].filter(page => !targetPage || page.pageNumber === targetPage)
			for (const page of pages) {
				const match = markRunsByText(page.runs, target)
				if (match) {
					scrollElementIntoViewer(match)
					return true
				}
			}
			return false
		},
		[clearHighlight],
	)

	const scrollToPage = React.useCallback((pageNumber: number) => {
		pageRefs.current.get(pageNumber)?.root.scrollIntoView({ block: 'start', behavior: 'smooth' })
	}, [])

	const handlePageReady = React.useCallback((registration: PdfPageRegistration) => {
		pageRefs.current.set(registration.pageNumber, registration)
		setLoadedPages(pageRefs.current.size)
	}, [])

	React.useImperativeHandle(ref, () => ({ highlightCitation, clearHighlight, scrollToPage }), [
		clearHighlight,
		highlightCitation,
		scrollToPage,
	])

	React.useEffect(() => {
		let cancelled = false
		pageRefs.current.clear()
		setDocumentProxy(null)
		setLoadedPages(0)
		setError(null)

		const loadingTask = getDocument({ url })
		loadingTask.promise
			.then(pdf => {
				if (!cancelled) setDocumentProxy(pdf)
			})
			.catch((cause: unknown) => {
				if (!cancelled) setError(cause instanceof Error ? cause.message : 'Revisá que el enlace del PDF sea accesible.')
			})

		return () => {
			cancelled = true
			void loadingTask.destroy()
		}
	}, [url])

	const ready = Boolean(documentProxy && loadedPages === documentProxy.numPages)
	useInitialHighlight(initialHighlight, highlightCitation, ready)
	useInitialPageScroll(initialPageNumber, scrollToPage, ready)

	return (
		<DocumentViewerShell
			className={className}
			status={
				documentProxy ? (
					<div className="rounded-full border border-slate-200 bg-white/90 px-3 py-1.5 text-[11px] font-medium text-slate-500 backdrop-blur">
						{loadedPages}/{documentProxy.numPages}
					</div>
				) : null
			}
		>
			{error ? (
				<ViewerError message={error} />
			) : (
				<div
					ref={containerRef}
					className="document-viewer-scroll custom-scrollbar h-full overflow-auto px-4 pb-12 pt-16"
				>
					{!documentProxy ? <ViewerLoading /> : null}
					<div className="document-viewer-pdf-pages mx-auto flex w-fit flex-col gap-5">
						{documentProxy
							? Array.from({ length: documentProxy.numPages }, (_, index) => (
									<PdfPageView
										key={`${url}:${index + 1}`}
										pdf={documentProxy}
										pageNumber={index + 1}
										onReady={handlePageReady}
									/>
								))
							: null}
					</div>
				</div>
			)}
		</DocumentViewerShell>
	)
})

function PdfPageView({
	pdf,
	pageNumber,
	onReady,
}: {
	pdf: PDFDocumentProxy
	pageNumber: number
	onReady: (registration: PdfPageRegistration) => void
}) {
	const rootRef = React.useRef<HTMLDivElement>(null)
	const canvasRef = React.useRef<HTMLCanvasElement>(null)
	const textLayerRef = React.useRef<HTMLDivElement>(null)
	const [page, setPage] = React.useState<PDFPageProxy | null>(null)
	const [width, setWidth] = React.useState(760)

	React.useEffect(() => {
		let cancelled = false
		void pdf.getPage(pageNumber).then(nextPage => {
			if (!cancelled) setPage(nextPage)
		})
		return () => {
			cancelled = true
		}
	}, [pageNumber, pdf])

	React.useEffect(() => {
		const root = rootRef.current
		if (!root) return
		const observer = new ResizeObserver(entries => {
			const nextWidth = entries[0]?.contentRect.width
			if (nextWidth) setWidth(Math.min(880, Math.max(320, nextWidth)))
		})
		observer.observe(root)
		return () => observer.disconnect()
	}, [])

	React.useEffect(() => {
		if (!page || !canvasRef.current || !textLayerRef.current || !rootRef.current) return
		let cancelled = false
		const canvas = canvasRef.current
		const context = canvas.getContext('2d')
		const textLayerElement = textLayerRef.current
		if (!context) return

		const unscaledViewport = page.getViewport({ scale: 1 })
		const scale = width / unscaledViewport.width
		const viewport = page.getViewport({ scale })
		const pixelRatio = window.devicePixelRatio || 1

		canvas.width = Math.floor(viewport.width * pixelRatio)
		canvas.height = Math.floor(viewport.height * pixelRatio)
		canvas.style.width = `${viewport.width}px`
		canvas.style.height = `${viewport.height}px`
		textLayerElement.style.width = `${viewport.width}px`
		textLayerElement.style.height = `${viewport.height}px`
		textLayerElement.style.setProperty('--scale-factor', String(scale))

		context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
		textLayerElement.replaceChildren()

		const renderTask = page.render({ canvasContext: context, viewport })
		const textPromise = page.getTextContent()

		Promise.all([renderTask.promise, textPromise])
			.then(([, textContent]) => {
				if (cancelled) return
				const textLayer = new TextLayer({
					textContentSource: textContent,
					container: textLayerElement,
					viewport,
				})
				return textLayer.render().then(() => {
					if (cancelled || !rootRef.current) return
					onReady({
						pageNumber,
						root: rootRef.current,
						runs: textLayer.textDivs.map((element, index) => ({
							element,
							text: textLayer.textContentItemsStr[index] ?? element.textContent ?? '',
						})),
					})
				})
			})
			.catch(() => undefined)

		return () => {
			cancelled = true
			renderTask.cancel()
		}
	}, [onReady, page, pageNumber, width])

	const aspectRatio = page ? page.getViewport({ scale: 1 }).width / page.getViewport({ scale: 1 }).height : 0.72

	return (
		<div ref={rootRef} className="document-viewer-pdf-page w-[min(78vw,880px)] max-w-full">
			<div
				className="relative overflow-hidden rounded-sm bg-white shadow-[0_2px_10px_rgba(15,23,42,0.08)] ring-1 ring-slate-200"
				style={{ aspectRatio }}
			>
				<canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />
				<div ref={textLayerRef} className="textLayer absolute inset-0" />
			</div>
			<p className="mt-2 text-center text-[11px] font-medium text-slate-400">Página {pageNumber}</p>
		</div>
	)
}

export const DocxFileViewer = React.forwardRef<DocumentViewerHandle, DocumentViewerProps>(function DocxFileViewer(
	{ url, initialHighlight, initialPageNumber, className },
	ref,
) {
	const scrollRef = React.useRef<HTMLDivElement>(null)
	const bodyRef = React.useRef<HTMLDivElement>(null)
	const styleRef = React.useRef<HTMLDivElement>(null)
	const [loading, setLoading] = React.useState(true)
	const [error, setError] = React.useState<string | null>(null)

	const clearHighlight = React.useCallback(() => {
		if (bodyRef.current) clearMarkedElements(bodyRef.current)
	}, [])

	const highlightCitation = React.useCallback(
		(target: DocumentHighlightTarget) => {
			if (!bodyRef.current) return false
			clearHighlight()
			const textMatch = markTextMatchInRoot(bodyRef.current, target)
			if (textMatch) {
				scrollElementIntoViewer(textMatch)
				return true
			}

			const blockMatch = markBestBlockMatch(bodyRef.current, target)
			if (blockMatch) {
				scrollElementIntoViewer(blockMatch)
				return true
			}

			return false
		},
		[clearHighlight],
	)

	const scrollToPage = React.useCallback((pageNumber: number) => {
		const pages = bodyRef.current?.querySelectorAll<HTMLElement>(
			'.docx-wrapper > section, .ius-docx-wrapper > section, section.docx, section.ius-docx',
		)
		pages?.[pageNumber - 1]?.scrollIntoView({ block: 'start', behavior: 'smooth' })
	}, [])

	React.useImperativeHandle(ref, () => ({ highlightCitation, clearHighlight, scrollToPage }), [
		clearHighlight,
		highlightCitation,
		scrollToPage,
	])

	React.useEffect(() => {
		let cancelled = false
		const abortController = new AbortController()
		setLoading(true)
		setError(null)
		bodyRef.current?.replaceChildren()
		styleRef.current?.replaceChildren()

		fetch(url, { signal: abortController.signal })
			.then(response => {
				if (!response.ok) throw new Error(`HTTP ${response.status}`)
				return response.blob()
			})
			.then(blob => {
				if (cancelled || !bodyRef.current || !styleRef.current) return
				return renderDocxAsync(blob, bodyRef.current, styleRef.current, {
					className: 'ius-docx',
					breakPages: true,
					experimental: true,
					ignoreFonts: false,
					ignoreHeight: false,
					ignoreWidth: false,
					inWrapper: true,
					renderComments: false,
					renderFooters: true,
					renderHeaders: true,
				})
			})
			.then(() => {
				if (!cancelled) setLoading(false)
			})
			.catch((cause: unknown) => {
				if (cancelled || abortController.signal.aborted) return
				setError(cause instanceof Error ? cause.message : 'Revisá que el enlace del DOCX sea accesible.')
				setLoading(false)
			})

		return () => {
			cancelled = true
			abortController.abort()
		}
	}, [url])

	useInitialHighlight(initialHighlight, highlightCitation, !loading && !error)
	useInitialPageScroll(initialPageNumber, scrollToPage, !loading && !error)

	return (
		<DocumentViewerShell className={className}>
			{error ? (
				<ViewerError message={error} />
			) : (
				<div ref={scrollRef} className="document-viewer-scroll custom-scrollbar h-full overflow-auto px-4 pb-12 pt-16">
					{loading ? <ViewerLoading label="Componiendo documento Word" /> : null}
					<div ref={styleRef} />
					<div ref={bodyRef} className="docx-viewer-body mx-auto w-fit max-w-full" />
				</div>
			)}
		</DocumentViewerShell>
	)
})

const documentViewerCss = `
.document-viewer-scroll { scroll-padding: 7rem 2rem 4rem; }
.textLayer {
	position: absolute;
	inset: 0;
	overflow: hidden;
	line-height: 1;
	text-align: initial;
	text-size-adjust: none;
	forced-color-adjust: none;
	transform-origin: 0 0;
}
.textLayer :is(span, br) {
	position: absolute;
	color: transparent;
	white-space: pre;
	cursor: text;
	transform-origin: 0% 0%;
}
.textLayer span::selection {
	background: color-mix(in oklch, #f5c542 55%, transparent);
}
.document-citation-highlight {
	background: color-mix(in oklch, #bae6fd 70%, transparent) !important;
	border-radius: 3px;
	color: inherit;
	animation: documentCitationPulse 900ms cubic-bezier(.22, 1, .36, 1);
}
.textLayer .document-citation-highlight {
	color: transparent !important;
}
.document-citation-highlight-block {
	background: transparent !important;
	outline: 2px solid color-mix(in oklch, #38bdf8 58%, transparent);
	outline-offset: 5px;
	border-radius: 4px;
	animation: documentCitationPulse 900ms cubic-bezier(.22, 1, .36, 1);
}
.docx-viewer-body .docx-wrapper,
.docx-viewer-body .ius-docx-wrapper {
	background: transparent !important;
	padding: 0 !important;
	padding-bottom: 0 !important;
	display: flex;
	flex-flow: column;
	align-items: center;
}
.docx-viewer-body .docx-wrapper > section,
.docx-viewer-body .ius-docx-wrapper > section,
.docx-viewer-body section.docx,
.docx-viewer-body section.ius-docx {
	margin: 0 auto 1.25rem !important;
	background: white !important;
	box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06) !important;
	border: 1px solid rgb(226 232 240);
}
.document-viewer-wrap-text .document-viewer-scroll {
	overflow-x: hidden;
	overscroll-behavior: contain;
	padding-left: 0.75rem;
	padding-right: 0.75rem;
}
.document-viewer-wrap-text .document-viewer-pdf-pages {
	width: 100% !important;
	max-width: 100% !important;
}
.document-viewer-wrap-text .document-viewer-pdf-page {
	width: 100% !important;
	max-width: 100% !important;
}
.document-viewer-wrap-text .docx-viewer-body {
	width: 100% !important;
	max-width: 100% !important;
	overflow-x: hidden !important;
}
.document-viewer-wrap-text .docx-viewer-body .docx-wrapper,
.document-viewer-wrap-text .docx-viewer-body .ius-docx-wrapper {
	width: 100% !important;
	max-width: 100% !important;
	align-items: stretch;
}
.document-viewer-wrap-text .docx-viewer-body .docx-wrapper > section,
.document-viewer-wrap-text .docx-viewer-body .ius-docx-wrapper > section,
.document-viewer-wrap-text .docx-viewer-body section.docx,
.document-viewer-wrap-text .docx-viewer-body section.ius-docx {
	box-sizing: border-box !important;
	width: 100% !important;
	max-width: 100% !important;
	min-width: 0 !important;
	overflow: visible !important;
}
.document-viewer-wrap-text .docx-viewer-body table {
	width: 100% !important;
	max-width: 100% !important;
	table-layout: fixed !important;
}
.document-viewer-wrap-text .docx-viewer-body :is(p, div, span, table, tbody, thead, tr, td, th, li) {
	white-space: normal !important;
	min-width: 0 !important;
}
.document-viewer-wrap-text .docx-viewer-body :is(td, th) {
	overflow-wrap: anywhere !important;
	word-break: break-word !important;
}
.document-viewer-wrap-text .docx-viewer-body * {
	max-width: 100% !important;
	overflow-wrap: anywhere !important;
	word-break: normal;
}
@keyframes documentCitationPulse {
	0% { filter: saturate(1.4); transform: scale(1.008); }
	100% { filter: saturate(1); transform: scale(1); }
}
@media (prefers-reduced-motion: reduce) {
	.document-citation-highlight,
	.document-citation-highlight-block {
		animation: none;
	}
}
`
